import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Card from "../../components/common/Card";
import Table from "../../components/common/Table";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import ProcessPaymentModal from "../../components/pos/ProcessPaymentModal";
import { normalizeMoneyAmount } from "../../utils/money";
import {
  useGetPosOrdersQuery,
  useLazyGetPosOrderInvoicesQuery,
  useProcessPosInvoicePaymentMutation,
  useProcessPosOrderPaymentMutation,
} from "../../store/api";
import { formatApiDateTime } from "../../utils/dateTime";
import {
  buildPaymentSignature,
  isRetryablePaymentError,
  nextIdempotencyState,
} from "../../utils/paymentIdempotency";

const normalizeEnum = (value) => String(value ?? "").trim().toUpperCase();

const isQrOrKiosk = (channel) => {
  const normalized = normalizeEnum(channel);
  return normalized === "QR" || normalized === "KIOSK";
};

const paymentNeedsCollection = (order) => {
  if (!order) return false;
  if (!isQrOrKiosk(order.channel)) return false;
  const status = normalizeEnum(order.paymentStatus);
  return status === "UNPAID" || status === "PARTIAL";
};

const isInvoiceScopeRequiredError = (err) => {
  const code = err?.data?.code || err?.data?.title || err?.data?.errorCode;
  return (
    err?.status === 409 &&
    normalizeEnum(code) === "INVOICE_SCOPE_REQUIRED"
  );
};

const extractApiMessage = (err) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.error ||
  "Payment failed. Please try again.";

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getMasterTokenNo = (order) =>
  String(
    order?.masterTokenNo ??
      order?.masterToken ??
      order?.tokenNo ??
      order?.tokenNumber ??
      "",
  ).trim();

const getDisplayOrderNo = (order) =>
  String(order?.orderNo ?? order?.orderNumber ?? order?.orderId ?? "").trim();

const getOrderPaymentAmount = (order) => {
  const grandTotal = Number(
    order?.grandTotal ?? order?.totals?.grandTotal ?? order?.totals?.total ?? 0,
  );
  const paidAmount = Number(order?.paidAmount ?? order?.totals?.paidAmount ?? 0);
  return normalizeMoneyAmount(Math.max(grandTotal - paidAmount, 0));
};

const getInvoicePaymentAmount = (invoice) => {
  const grandTotal = Number(invoice?.grandTotal ?? 0);
  const paidAmount = Number(invoice?.paidAmount ?? 0);
  return normalizeMoneyAmount(Math.max(grandTotal - paidAmount, 0));
};

const PaymentStatusBadge = ({ value }) => {
  const status = normalizeEnum(value);
  if (!status) return <span className="text-slate-400">-</span>;

  const isUnpaid = status === "UNPAID";
  const isPartial = status === "PARTIAL";
  const className = [
    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
    isUnpaid
      ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-200"
      : isPartial
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200"
        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
  ].join(" ");

  return <span className={className}>{status}</span>;
};

const PendingPayments = () => {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [invoiceFlow, setInvoiceFlow] = useState(null); // { orderId, invoices }
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);
  const lastInvoicePaymentAttemptRef = useRef(null);

  const queryArgs = useMemo(
    () => ({
      channels: "QR,KIOSK",
      page: 1,
      pageSize: 100,
    }),
    [],
  );

  const {
    data: ordersData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetPosOrdersQuery(queryArgs);

  const [processOrderPayment, { isLoading: isPayingOrder }] =
    useProcessPosOrderPaymentMutation();
  const [processInvoicePayment, { isLoading: isPayingInvoice }] =
    useProcessPosInvoicePaymentMutation();
  const [fetchInvoices] = useLazyGetPosOrderInvoicesQuery();

  const orders = Array.isArray(ordersData?.orders)
    ? ordersData.orders
    : Array.isArray(ordersData)
      ? ordersData
      : [];

  const unpaidOrders = orders.filter(paymentNeedsCollection);

  const columns = [
    { key: "orderNo", label: "Order No" },
    { key: "orderId", label: "Order ID" },
    { key: "payment", label: "Payment" },
    { key: "channel", label: "Channel" },
    { key: "customer", label: "Customer" },
    { key: "total", label: "Total" },
    { key: "placedAt", label: "Placed At" },
    { key: "actions", label: "Actions" },
  ];

  const rows = unpaidOrders.map((order) => {
    const highlight =
      normalizeEnum(order.paymentStatus) === "UNPAID" ||
      normalizeEnum(order.paymentStatus) === "PARTIAL";

    const rowClassName = highlight
      ? "bg-rose-50/60 dark:bg-rose-950/20"
      : undefined;

    return {
      id: order.orderId,
      className: rowClassName,
      orderNo: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {getMasterTokenNo(order) || "-"}
        </span>
      ),
      orderId: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          #{getDisplayOrderNo(order) || "-"}
        </span>
      ),
      payment: <PaymentStatusBadge value={order.paymentStatus} />,
      channel: order.channel || "-",
      customer: order.customerName || "-",
      total: (
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {formatMoney(order.grandTotal)}
        </span>
      ),
      placedAt: formatApiDateTime(order.placedAt),
      actions: (
        <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              navigate(
                `/fulfillment/orders/${order.orderId}?source=pending-payments`,
              )
            }
          >
            View Details
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setModalError(null);
              setSuccessMessage(null);
              setInvoiceFlow(null);
              setSelectedOrder(order);
            }}
          >
            Take Payment
          </Button>
        </div>
      ),
    };
  });

  const closeModal = () => {
    setSelectedOrder(null);
    setModalError(null);
    setSuccessMessage(null);
    setInvoiceFlow(null);
    setActivePaymentMethod(null);
  };

  const payOrder = async (method, amount) => {
    if (!selectedOrder?.orderId) return;
    setModalError(null);
    setSuccessMessage(null);
    setActivePaymentMethod(method);

    try {
      const paymentAmount =
        amount === null || amount === undefined
          ? getOrderPaymentAmount(selectedOrder)
          : normalizeMoneyAmount(amount);

      await processOrderPayment({
        orderId: selectedOrder.orderId,
        payload: { method, amount: paymentAmount },
      }).unwrap();

      setSuccessMessage("Payment successful.");
      await refetch();
      setTimeout(() => closeModal(), 600);
    } catch (err) {
      if (isInvoiceScopeRequiredError(err)) {
        try {
          const invoices = await fetchInvoices(selectedOrder.orderId).unwrap();
          const list = Array.isArray(invoices) ? invoices : [];
          setInvoiceFlow({ orderId: selectedOrder.orderId, invoices: list });
          setModalError(
            "This order requires invoice-based payment. Select an invoice to pay.",
          );
          return;
        } catch (invoiceErr) {
          setModalError(extractApiMessage(invoiceErr));
          return;
        }
      }

      setModalError(extractApiMessage(err));
    } finally {
      setActivePaymentMethod(null);
    }
  };

  const payInvoice = async ({ invoiceId, method, amount }) => {
    if (!invoiceId) return;
    setModalError(null);
    setSuccessMessage(null);

    try {
      const targetInvoice = invoicesForModal.find(
        (invoice) => invoice.invoiceId === invoiceId,
      );
      const paymentAmount =
        amount === null || amount === undefined
          ? getInvoicePaymentAmount(targetInvoice)
          : normalizeMoneyAmount(amount);
      const signature = buildPaymentSignature({
        scope: `INVOICE:${invoiceId}`,
        method,
        amount: paymentAmount,
      });
      const attempt = nextIdempotencyState(
        lastInvoicePaymentAttemptRef.current,
        signature,
      );
      lastInvoicePaymentAttemptRef.current = attempt;

      await processInvoicePayment({
        invoiceId,
        payload: { method, amount: paymentAmount },
      }).unwrap();
      lastInvoicePaymentAttemptRef.current = null;

      setSuccessMessage("Payment successful.");
      await refetch();
      setTimeout(() => closeModal(), 600);
    } catch (err) {
      if (lastInvoicePaymentAttemptRef.current) {
        lastInvoicePaymentAttemptRef.current = {
          ...lastInvoicePaymentAttemptRef.current,
          retryAllowed: isRetryablePaymentError(err),
        };
      }
      setModalError(extractApiMessage(err));
    }
  };

  const invoicesForModal = invoiceFlow?.invoices || [];
  const openInvoices = invoicesForModal.filter(
    (inv) => normalizeEnum(inv?.status) === "OPEN",
  );
  const isPaying = isPayingOrder || isPayingInvoice;
  const modalTotal = getOrderPaymentAmount(selectedOrder);
  const orderGrandTotal = Number(
    selectedOrder?.grandTotal ??
      selectedOrder?.totals?.grandTotal ??
      selectedOrder?.totals?.total ??
      0,
  );
  const orderTax = Number(
    selectedOrder?.taxAmount ?? selectedOrder?.totals?.taxAmount ?? 0,
  );
  const orderSubtotal = Number(
    selectedOrder?.subtotal ??
      selectedOrder?.totals?.subtotal ??
      Math.max(orderGrandTotal - orderTax, 0),
  );

  return (
    <div className="space-y-4 px-6 py-6">
      <PageHeader
        title="Pending Payments"
        subtitle="QR/Kiosk orders awaiting payment collection."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Refresh
            </Button>
          </div>
        }
      />

      <Card className="space-y-3">
        {isLoading && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading pending payments...
          </p>
        )}
        {isError && (
          <Alert tone="error">
            {error?.data?.detail ||
              error?.data?.message ||
              "Failed to load pending payments."}
          </Alert>
        )}
        {!isLoading && !isError && (
          <Table
            columns={columns}
            rows={rows}
            emptyText="No unpaid QR/Kiosk orders."
          />
        )}
      </Card>

      <ProcessPaymentModal
        isOpen={Boolean(selectedOrder) && !invoiceFlow}
        onClose={closeModal}
        subtotal={orderSubtotal}
        tax={orderTax}
        total={orderGrandTotal || modalTotal}
        amountDue={modalTotal}
        isProcessing={isPayingOrder}
        activePaymentMethod={activePaymentMethod}
        paymentError={modalError}
        paymentSuccess={successMessage}
        showAmountEntry={false}
        showKeypad={false}
        tipConfig={{ enabled: false }}
        onCash={(amount) => payOrder("CASH", amount)}
        onCard={(amount) => payOrder("CARD", amount)}
      />

      <Modal
        isOpen={Boolean(invoiceFlow)}
        onClose={closeModal}
        title="Select Invoice"
      >
        <div className="space-y-4">
          {modalError && <Alert tone="error">{modalError}</Alert>}
          {successMessage && <Alert tone="success">{successMessage}</Alert>}

          {selectedOrder && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-slate-700 dark:text-slate-300">
                  <span className="font-semibold text-slate-900 dark:text-slate-100">
                    Order:
                  </span>{" "}
                  #{getDisplayOrderNo(selectedOrder) || "-"}{" "}
                  {getMasterTokenNo(selectedOrder)
                    ? `(${getMasterTokenNo(selectedOrder)})`
                    : ""}
                </div>
                <div className="text-slate-500 dark:text-slate-400">
                  Channel:{" "}
                  <span className="font-semibold text-slate-700 dark:text-slate-200">
                    {selectedOrder.channel || "-"}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Invoices
              </div>
              {openInvoices.length === 0 ? (
                <Alert tone="warning">
                  No OPEN invoices found for this order. Please refresh and try again.
                </Alert>
              ) : (
                <div className="space-y-2">
                  {openInvoices.map((invoice) => (
                    <div
                      key={invoice.invoiceId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          Invoice #{invoice.invoiceNo ?? "-"}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          Status: {invoice.status || "-"} • Total:{" "}
                          {formatMoney(invoice.grandTotal)}
                        </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            payInvoice({
                              invoiceId: invoice.invoiceId,
                              method: "CASH",
                              amount: null,
                            })
                          }
                          disabled={isPaying}
                          className="border-emerald-200 bg-emerald-100 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/60 font-bold"
                        >
                          CASH
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            payInvoice({
                              invoiceId: invoice.invoiceId,
                              method: "CARD",
                              amount: null,
                            })
                          }
                          disabled={isPaying}
                          className="border-indigo-200 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 hover:text-indigo-800 dark:border-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 dark:hover:bg-indigo-900/60 font-bold"
                        >
                          CARD
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PendingPayments;
